// ============================================================================
// 📄 src/pages/Finance/NouvelleFacture.tsx — EspoirAfriqueSas Platinum PRO 2025+
// 💎 Création + Aperçu PDF + Joindre preuve de paiement post-enregistrement
// ============================================================================
import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../../../lib/axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import dayjs from "dayjs";

interface Client {
  id: number;
  nom?: string;
  prenom?: string;
  nomEntreprise?: string;
}

interface Ligne {
  description: string;
  quantite: number;
  prixUnitaire: number;
  total: number;
}
 
const NouvelleFacture: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isExceptionnel = searchParams.get("exceptionnel") === "1";

  const [clients, setClients] = useState<Client[]>([]);
  const [form, setForm] = useState({
    clientId: "",
    dateEcheance: dayjs().add(7, "day").format("YYYY-MM-DD"),
    devise: "USD",
    remise: 0,
    tva: 16,
  });

  const [lignes, setLignes] = useState<Ligne[]>([]);
  const [totaux, setTotaux] = useState({ ht: 0, tva: 0, ttc: 0 });

  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [showPreuveModal, setShowPreuveModal] = useState(false);
  const [lastFactureId, setLastFactureId] = useState<number | null>(null);
  const [preuveFile, setPreuveFile] = useState<File | null>(null);
  const [montant, setMontant] = useState<number>(0);

  // ============================================================
  // 🔁 Chargement des clients sécurisé
  // ============================================================
  useEffect(() => {
    const controller = new AbortController();
    const fetchClients = async () => {
      try {
        const res = await api.get<{ data?: Client[]; items?: Client[] }>("/clients", {
          signal: controller.signal,
        });
        const raw = res.data as Record<string, unknown> | undefined;
        const list = Array.isArray(raw?.data)
          ? (raw?.data as Client[])
          : Array.isArray(raw?.items)
          ? (raw?.items as Client[])
          : Array.isArray(raw)
          ? (raw as unknown as Client[])
          : [];
        setClients(list);
      } catch {
        toast.error("Erreur lors du chargement des clients.");
      }
    };
    fetchClients();
    return () => controller.abort();
  }, []);

  // ============================================================
  // 🧮 Calculs totaux dynamiques
  // ============================================================
  useEffect(() => {
    const ht = lignes.reduce((sum, l) => sum + (l.quantite * l.prixUnitaire || 0), 0);
    const montantTVA = ((ht - form.remise) * form.tva) / 100;
    const ttc = ht - form.remise + montantTVA;
    setTotaux({ ht, tva: montantTVA, ttc });
  }, [lignes, form.tva, form.remise]);

  // ============================================================
  // ➕ Gestion des lignes
  // ============================================================
  const ajouterLigne = () =>
    setLignes((prev) => [...prev, { description: "", quantite: 1, prixUnitaire: 0, total: 0 }]);

  const modifierLigne = (i: number, key: keyof Ligne, value: string | number) => {
    const updated = [...lignes];
    updated[i] = { ...updated[i], [key]: key === "description" ? value : Number(value) };
    updated[i].total = updated[i].quantite * updated[i].prixUnitaire;
    setLignes(updated);
  };

  const supprimerLigne = (i: number) =>
    setLignes((prev) => prev.filter((_, idx) => idx !== i));

  // ============================================================
  // 🧾 Aperçu PDF
  // ============================================================
  const genererPDF = useCallback(() => {
    const client = clients.find((c) => c.id === Number(form.clientId));
    if (!client) return toast.warning("Sélectionnez un client avant l’aperçu.");

    const doc = new jsPDF();
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text("FACTURE", 14, 20);

    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.text(`Client : ${client.nomEntreprise || client.nom || "—"}`, 14, 30);
    doc.text(`Date : ${dayjs().format("DD/MM/YYYY")}`, 14, 36);
    doc.text(`Échéance : ${dayjs(form.dateEcheance).format("DD/MM/YYYY")}`, 14, 42);
    doc.text(`Devise : ${form.devise}`, 14, 48);

    const lignesData = lignes.map((l) => [
      l.description || "—",
      l.quantite,
      l.prixUnitaire.toLocaleString(),
      l.total.toLocaleString(),
    ]);

    autoTable(doc, {
      head: [["Désignation", "Qté", "PU", "Total"]],
      body: lignesData,
      startY: 60,
      styles: { halign: "center" },
      headStyles: { fillColor: [240, 180, 60] },
    });

    const finalY = (doc as any).lastAutoTable.finalY || 60;
    doc.setFontSize(11);
    doc.text(`Sous-total (HT): ${totaux.ht.toLocaleString()} ${form.devise}`, 130, finalY + 10);
    doc.text(`TVA (${form.tva}%): ${totaux.tva.toLocaleString()} ${form.devise}`, 130, finalY + 16);
    doc.text(`Remise: ${form.remise.toLocaleString()} ${form.devise}`, 130, finalY + 22);
    doc.setFont("helvetica", "bold");
    doc.text(`Total TTC: ${totaux.ttc.toLocaleString()} ${form.devise}`, 130, finalY + 30);

    const pdfBlob = doc.output("blob");
    const url = URL.createObjectURL(pdfBlob);
    setPdfUrl(url);
    setShowPreview(true);
  }, [clients, form, lignes, totaux]);

  // ============================================================
  // 💾 Enregistrement facture
  // ============================================================
const validerFacture = async () => {
  if (!form.clientId || lignes.length === 0)
    return toast.warning("Complétez tous les champs.");

  setLoading(true);

  try {
    const res = await api.post("/factures", {
      clientId: Number(form.clientId),
      dateEcheance: form.dateEcheance,
      devise: form.devise,
      remise: form.remise,
      tva: form.tva,
      lignes,
    });

    const payload = res.data as { facture?: { id: number }; message?: string };
    const newId = payload.facture?.id;
    if (!newId) {
      toast.error("Réponse serveur invalide (facture sans identifiant).");
      return;
    }

    setLastFactureId(newId);
    toast.success(payload.message ?? "Facture créée. Le PDF est en cours de génération.");
    setShowPreuveModal(true);

  } catch (err) {
    toast.error("Erreur création facture.");
  } finally {
    setLoading(false);
  }
};

  // ============================================================
  // 📎 Upload preuve
  // ============================================================
  const envoyerPreuve = async () => {
    if (!lastFactureId || !preuveFile)
      return toast.warning("Veuillez sélectionner un fichier.");
    if (!preuveFile.type.startsWith("image/") && !preuveFile.type.includes("pdf"))
      return toast.error("Seuls les fichiers PDF ou images sont acceptés.");

    const formData = new FormData();
    formData.append("fichier", preuveFile);
    formData.append("montant", String(montant));

    try {
      await api.post(`/factures/${lastFactureId}/preuves`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("📎 Preuve enregistrée !");
      setShowPreuveModal(false);
      setTimeout(() => navigate("/admin/finance/factures"), 600);
    } catch {
      toast.error("Erreur lors de l’upload de la preuve.");
    }
  };

  // ============================================================
  // 🎨 Interface utilisateur
  // ============================================================
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0b0c0e] via-[#111315] to-[#1a1c1f] text-gray-100 p-6 md:p-10">
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between mb-6"
      >
        <h1 className="text-3xl font-extrabold text-amber-400 flex items-center gap-3">
          🧾 Nouvelle facture
        </h1>
        <button
          onClick={() => navigate("/admin/finance/factures")}
          className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg"
        >
          ← Retour
        </button>
      </motion.div>

      {isExceptionnel ? (
        <div className="mb-6 max-w-4xl mx-auto rounded-xl border border-amber-500/40 bg-amber-950/40 px-4 py-3 text-sm text-amber-100">
          <strong className="text-amber-50">Facture exceptionnelle</strong> — renseignez la justification en libellés
          / pièces jointes selon votre procédure interne. Ce flux contourne la facturation automatique abonnement /
          passages.
        </div>
      ) : null}

      {/* === FORMULAIRE === */}
      <div className="max-w-4xl mx-auto bg-slate-900/80 p-6 rounded-2xl border border-slate-700 shadow-2xl space-y-6">
        {/* Client */}
        <select
          value={form.clientId}
          onChange={(e) => setForm({ ...form, clientId: e.target.value })}
          className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-gray-100"
        >
          <option value="">-- Sélectionner un client --</option>
          {clients.map((c) => (
            <option key={c.id} value={c.id}>
              {c.nomEntreprise || `${c.prenom ?? ""} ${c.nom ?? ""}`.trim()}
            </option>
          ))}
        </select>

        {/* Dates / devise / remise */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <input
            type="date"
            value={form.dateEcheance}
            onChange={(e) => setForm({ ...form, dateEcheance: e.target.value })}
            className="bg-slate-800 border border-slate-700 rounded px-3 py-2 text-gray-100"
          />
          <select
            value={form.devise}
            onChange={(e) => setForm({ ...form, devise: e.target.value })}
            className="bg-slate-800 border border-slate-700 rounded px-3 py-2 text-gray-100"
          >
            <option value="USD">USD</option>
            <option value="CDF">CDF</option>
            <option value="EUR">EUR</option>
          </select>
          <input
            type="number"
            placeholder="Remise"
            value={form.remise}
            onChange={(e) => setForm({ ...form, remise: Number(e.target.value) })}
            className="bg-slate-800 border border-slate-700 rounded px-3 py-2 text-gray-100"
          />
        </div>

        {/* === Lignes === */}
        <div>
          <h2 className="text-lg font-bold text-amber-400 mb-3">Lignes</h2>
          {lignes.map((l, i) => (
            <div key={i} className="grid grid-cols-4 gap-2 items-center">
              <input
                value={l.description}
                onChange={(e) => modifierLigne(i, "description", e.target.value)}
                placeholder="Désignation"
                className="col-span-2 bg-slate-800 border border-slate-700 rounded px-2 py-1 text-gray-100"
              />
              <input
                type="number"
                value={l.quantite}
                onChange={(e) => modifierLigne(i, "quantite", e.target.value)}
                className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-right text-gray-100"
              />
              <input
                type="number"
                value={l.prixUnitaire}
                onChange={(e) => modifierLigne(i, "prixUnitaire", e.target.value)}
                className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-right text-gray-100"
              />
              <button
                onClick={() => supprimerLigne(i)}
                className="text-red-400 hover:text-red-300 text-xs"
              >
                Supprimer
              </button>
            </div>
          ))}
          <button
            onClick={ajouterLigne}
            className="text-blue-400 hover:underline text-sm mt-3"
          >
            + Ajouter une ligne
          </button>
        </div>

        {/* Totaux */}
        <div className="border-t border-slate-700 mt-5 pt-4 text-sm space-y-1">
          <p>
            HT :{" "}
            <span className="text-amber-300">
              {totaux.ht.toLocaleString()} {form.devise}
            </span>
          </p>
          <p>
            TVA ({form.tva}%) :{" "}
            <span className="text-amber-300">
              {totaux.tva.toLocaleString()} {form.devise}
            </span>
          </p>
          <p>
            Remise :{" "}
            <span className="text-amber-300">
              {form.remise.toLocaleString()} {form.devise}
            </span>
          </p>
          <h3 className="text-lg font-bold text-amber-400 mt-2">
            Total TTC : {totaux.ttc.toLocaleString()} {form.devise}
          </h3>
        </div>

        {/* Boutons */}
        <div className="flex justify-end gap-3 mt-5">
          <button
            onClick={genererPDF}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 rounded text-white font-semibold"
          >
            👁️ Aperçu PDF
          </button>
          <button
            disabled={loading}
            onClick={validerFacture}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 rounded text-white font-semibold disabled:opacity-50"
          >
            💾 {loading ? "Enregistrement..." : "Enregistrer"}
          </button>
        </div>
      </div>

      {/* === Modale preuve === */}
      <AnimatePresence>
        {showPreuveModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
          >
            <div className="bg-[#1b1c1f] p-6 rounded-xl shadow-xl w-[90%] md:w-[500px] space-y-4">
              <h2 className="text-xl font-bold text-amber-400">
                📎 Joindre une preuve de paiement
              </h2>
              <input
                type="file"
                onChange={(e) => setPreuveFile(e.target.files?.[0] || null)}
                className="block w-full text-sm text-gray-300"
              />
              <input
                type="number"
                placeholder="Montant payé"
                value={montant}
                onChange={(e) => setMontant(Number(e.target.value))}
                className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-gray-100"
              />
              <p className="text-sm text-gray-400">
                💡 Solde restant :{" "}
                <span className="text-amber-300 font-semibold">
                  {(totaux.ttc - montant).toLocaleString()} {form.devise}
                </span>
              </p>
              <div className="flex justify-end gap-3 mt-4">
                <button
                  onClick={() => setShowPreuveModal(false)}
                  className="px-3 py-2 bg-slate-700 rounded"
                >
                  Annuler
                </button>
                <button
                  onClick={envoyerPreuve}
                  className="px-3 py-2 bg-emerald-600 hover:bg-emerald-500 rounded text-white"
                >
                  ✅ Valider
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* === Aperçu PDF === */}
      <AnimatePresence>
        {showPreview && pdfUrl && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
          >
            <div className="bg-[#1b1c1f] p-5 rounded-xl shadow-xl w-11/12 h-[90vh]">
              <div className="flex justify-between mb-2">
                <h2 className="text-lg font-bold text-amber-400">
                  Aperçu de la facture
                </h2>
                <button
                  onClick={() => setShowPreview(false)}
                  className="text-red-400 hover:text-red-300 font-semibold"
                >
                  ✖ Fermer
                </button>
              </div>
              <iframe
                src={pdfUrl}
                className="w-full h-full rounded-md border border-slate-700"
                title="Aperçu PDF"
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
 
export default NouvelleFacture;